import { Link } from '@tanstack/react-router';
import { ChevronRight, Droplets, Utensils, Scale, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { ChecklistPond, CompletionStatus, SamplingStatus } from '@/types/entry';
import { format } from 'date-fns';

interface PondEntryCardProps {
  pond: ChecklistPond;
  date: Date;
  canEnter: boolean;
}

const statusConfig = {
  complete: {
    color: 'text-emerald-600',
    bg: 'bg-emerald-500',
    label: 'Done',
  },
  partial: {
    color: 'text-amber-600',
    bg: 'bg-amber-500',
    label: 'In Progress',
  },
  pending: {
    color: 'text-gray-400',
    bg: 'bg-gray-300',
    label: 'Pending',
  },
  not_due: {
    color: 'text-gray-300',
    bg: 'bg-gray-200',
    label: 'N/A',
  },
};

function getOverallStatus(completion: ChecklistPond['completion']): 'complete' | 'partial' | 'pending' {
  if (completion.water === 'complete' && completion.feeding === 'complete') {
    return 'complete';
  }
  if (completion.water === 'partial' || completion.feeding === 'partial' ||
      completion.water === 'complete' || completion.feeding === 'complete') {
    return 'partial';
  }
  return 'pending';
}

function StatusIndicator({
  status,
  icon: Icon,
  label
}: {
  status: CompletionStatus | SamplingStatus;
  icon: React.ElementType;
  label: string;
}) {
  const config = statusConfig[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn(
        'p-1.5 rounded-md transition-colors',
        status === 'complete' ? 'bg-emerald-50' :
        status === 'partial' ? 'bg-amber-50' : 'bg-gray-50'
      )}>
        <Icon className={cn('w-3.5 h-3.5', config.color)} />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={cn('text-xs font-medium', config.color)}>{config.label}</p>
      </div>
    </div>
  );
}

export function PondEntryCard({ pond, date, canEnter }: PondEntryCardProps) {
  const overallStatus = getOverallStatus(pond.completion);
  const dateStr = format(date, 'yyyy-MM-dd');

  const content = (
    <div className={cn(
      'relative overflow-hidden rounded-xl border bg-white transition-all duration-200',
      canEnter ? [
        'hover:shadow-lg hover:shadow-gray-900/10',
        'hover:border-gray-300',
        'hover:-translate-y-0.5',
        'cursor-pointer',
      ] : 'opacity-70',
      overallStatus === 'complete' && 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30'
    )}>
      {/* Status stripe */}
      <div className={cn(
        'absolute top-0 left-0 w-1 h-full',
        statusConfig[overallStatus].bg
      )} />

      <div className="p-4 pl-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 truncate">{pond.name}</h3>
              {overallStatus === 'complete' && (
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              )}
            </div>
            {pond.activeCycle ? (
              <p className="text-sm text-gray-500 mt-0.5">
                DOC <span className="font-medium text-gray-700">{pond.activeCycle.doc}</span>
              </p>
            ) : (
              <p className="text-sm text-gray-400 mt-0.5">No active cycle</p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ChevronRight className={cn(
              'w-5 h-5 transition-transform',
              canEnter ? 'text-gray-400 group-hover:translate-x-0.5' : 'text-gray-300'
            )} />
          </div>
        </div>

        {/* Status indicators */}
        {pond.activeCycle && (
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
            <StatusIndicator
              status={pond.completion.water}
              icon={Droplets}
              label="Water"
            />
            <StatusIndicator
              status={pond.completion.feeding}
              icon={Utensils}
              label="Feeding"
            />
            <StatusIndicator
              status={pond.completion.sampling}
              icon={Scale}
              label="Sampling"
            />
          </div>
        )}

        {/* Slot progress (for water readings) */}
        {pond.activeCycle && pond.expectedSlots.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500 mb-2">Reading slots</p>
            <div className="flex gap-1.5">
              {pond.expectedSlots.map((slot) => {
                const isComplete = pond.completedSlots.includes(slot);
                return (
                  <div
                    key={slot}
                    className={cn(
                      'flex-1 py-1 px-1.5 rounded text-center text-xs font-medium transition-colors',
                      isComplete
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-400'
                    )}
                  >
                    {slot}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (!canEnter || !pond.activeCycle) {
    return <div className="group">{content}</div>;
  }

  return (
    <Link
      to="/entry/pond/$pondId"
      params={{ pondId: pond.id }}
      search={{ date: dateStr }}
      className="group block"
    >
      {content}
    </Link>
  );
}
